import { useMemo, useState } from "react";
import { MoreHorizontal, RefreshCw, Search } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { useCompanies, type CompanyRow } from "@/hooks/useCompanies";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const StatusBadge = ({ company }: { company: CompanyRow }) => {
  if (company.status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (company.status === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended{company.suspension_type ? ` (${company.suspension_type})` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 border border-slate-600">
      {company.status ?? "Unknown"}
    </span>
  );
};

const SkeletonCell = ({ w = "w-20" }: { w?: string }) => (
  <div className={`h-4 ${w} rounded bg-slate-700 animate-pulse`} />
);

export default function Companies() {
  const { companies, isLoading, error, refetch, isFetching } = useCompanies();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState("");
  const [suspendTarget, setSuspendTarget] = useState<CompanyRow | null>(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState<CompanyRow | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name?.toLowerCase().includes(q));
  }, [companies, filter]);

  const openSuspend = (c: CompanyRow) => {
    setReason("");
    setSuspendTarget(c);
  };
  const closeSuspend = () => {
    if (submitting) return;
    setSuspendTarget(null);
    setReason("");
  };
  const openUnsuspend = (c: CompanyRow) => setUnsuspendTarget(c);
  const closeUnsuspend = () => {
    if (submitting) return;
    setUnsuspendTarget(null);
  };

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    const trimmed = reason.trim();
    if (!trimmed) return;
    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("suspend_company_members", {
      p_company_id: suspendTarget.id,
      p_suspension_type: "manual",
      p_suspension_reason: trimmed,
    });
    setSubmitting(false);
    if (rpcError) {
      toast.error(rpcError.message);
      return;
    }
    toast.success("Company suspended");
    setSuspendTarget(null);
    setReason("");
    queryClient.invalidateQueries({ queryKey: ["companies"] });
  };

  const handleUnsuspend = async () => {
    if (!unsuspendTarget) return;
    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("unsuspend_company_members", {
      p_company_id: unsuspendTarget.id,
    });
    setSubmitting(false);
    if (rpcError) {
      toast.error(rpcError.message);
      return;
    }
    toast.success("Company reactivated");
    setUnsuspendTarget(null);
    queryClient.invalidateQueries({ queryKey: ["companies"] });
  };

  return (
    <AdminLayout title="Companies">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 max-w-md space-y-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by company name…"
                className="pl-8 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>
            {!isLoading && !error && (
              <div className="text-xs text-slate-500">
                Showing {filtered.length} of {companies.length} companies
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center h-9 w-9 rounded border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 disabled:opacity-50"
            aria-label="Refresh companies"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Table */}
        <div className="border border-slate-800 rounded-md overflow-hidden bg-slate-900">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Company Name</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Members</th>
                <th className="text-left font-semibold px-4 py-2.5">Admins</th>
                <th className="text-left font-semibold px-4 py-2.5">Created</th>
                <th className="text-right font-semibold px-4 py-2.5 w-12">Actions</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <p className="text-sm text-red-400">{error}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                        className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                      >
                        Retry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="px-4 py-3"><SkeletonCell w="w-40" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-20" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-8" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-8" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-24" /></td>
                    <td className="px-4 py-3"><SkeletonCell w="w-6" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                    No companies match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-slate-800 last:border-0 hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{c.name}</span>
                        {c.hasNoAdmin && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-500/15 text-red-400 border border-red-500/30">
                            No admins
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge company={c} /></td>
                    <td className="px-4 py-3 text-slate-300">{c.memberCount}</td>
                    <td className={`px-4 py-3 ${c.adminCount === 0 ? "text-red-400 font-semibold" : "text-slate-300"}`}>
                      {c.adminCount}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-7 w-7 rounded text-slate-400 hover:bg-slate-700 hover:text-white"
                            aria-label="Open actions menu"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-slate-800 border-slate-700 text-slate-100"
                        >
                          {c.status === "active" && (
                            <DropdownMenuItem
                              onClick={() => openSuspend(c)}
                              className="text-amber-400 focus:bg-slate-700 focus:text-amber-300"
                            >
                              Suspend Company
                            </DropdownMenuItem>
                          )}
                          {c.status === "suspended" && (
                            <DropdownMenuItem
                              onClick={() => openUnsuspend(c)}
                              className="text-emerald-400 focus:bg-slate-700 focus:text-emerald-300"
                            >
                              Unsuspend Company
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => console.log("View members for", c.id)}
                            className="focus:bg-slate-700"
                          >
                            View Members
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend dialog */}
      <Dialog
        open={!!suspendTarget}
        onOpenChange={(open) => { if (!open) closeSuspend(); }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Suspend {suspendTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Suspending this company will immediately block access for all{" "}
              {suspendTarget?.memberCount ?? 0} members. They will see a suspended
              account screen until the company is reactivated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="suspend-reason" className="text-slate-300">
              Reason for suspension
            </Label>
            <Input
              id="suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you suspending this company?"
              className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeSuspend}
              disabled={submitting}
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={submitting || !reason.trim()}
            >
              {submitting ? "Suspending…" : "Suspend Company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsuspend dialog */}
      <Dialog
        open={!!unsuspendTarget}
        onOpenChange={(open) => { if (!open) closeUnsuspend(); }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              Unsuspend {unsuspendTarget?.name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will restore access for all members who were suspended when the
              company was suspended.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeUnsuspend}
              disabled={submitting}
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnsuspend}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {submitting ? "Reactivating…" : "Unsuspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
